import { WalletAddress } from '../Ether';

export enum ActivityType {
  Welcome = 'welcome',
  Buy = 'buy',
  Sell = 'sell',
  Visit = 'visit',
  PriceWentUp = 'price_went_up',
  Follow = 'follow',
  FollowBack = 'follow_back',
  FollowingCreatedGem = 'following_created_gem',
  LikedGame = 'liked_game',
  CommentedGame = 'commented_game',
  GemUnlockedWithHelp = 'gem_unlocked_with_help',
  GemBoughtWithHelp = 'gem_bought_with_help',
  ChatNewMessage = 'chat_new_message',
}

export interface ActivitySchema {
  activity_id: number;
  type: ActivityType;
  user_id: number;
  created_at?: string;
  read_at?: string;
  username?: string;
  offchain_game_id?: number;
  game_id?: number;
  gem_name?: string;
  gem_type?: string;
  participant_name?: string;
  participant_id?: number;
  participant_image?: string;
  participant_wallet?: WalletAddress;
  participant_displayname?: string;
  price?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extra?: Record<string, any>;
}

export interface PendingActivitySchema extends ActivitySchema {
  pending?: boolean;
  pending_notif_message?: string;
  pending_notif_header?: string;
}

export type InsertActivitySchema = Omit<ActivitySchema, 'activity_id'>;

export type InsertPendingActivitySchema = Omit<PendingActivitySchema, 'activity_id'>;

export interface ActivityData {
  activityId: number;
  type: ActivityType;
  userId: number;
  createdAt?: string;
  readAt?: string;
  username?: string;
  offChainGameId?: number;
  gameId?: number;
  gemName?: string;
  gemType?: string;
  participantId?: number;
  participantName?: string;
  participantImage?: string;
  participantWallet?: WalletAddress;
  participantDisplayName?: string;
  price?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extra?: Record<string, any>;
}

export interface PendingActivityData extends ActivityData {
  pending?: boolean;
  pendingNotifMessage?: string;
  pendingNotifHeader?: string;
}

export type InsertActivityData = Omit<ActivityData, 'activityId'>;

export type InsertPendingActivityData = Omit<PendingActivityData, 'activityId'>;

function castNumOrUndefined(num: unknown | undefined): number | undefined {
  return num !== undefined ? Number(num) : undefined;
}

export const getActivityDataFromSchema = (schema: ActivitySchema): ActivityData => ({
  activityId: Number(schema.activity_id),
  type: schema.type,
  userId: Number(schema.user_id),
  createdAt: schema.created_at,
  readAt: schema.read_at,
  username: schema.username,
  offChainGameId: castNumOrUndefined(schema.offchain_game_id),
  gameId: castNumOrUndefined(schema.game_id),
  gemName: schema.gem_name,
  gemType: schema.gem_type,
  participantId: castNumOrUndefined(schema.participant_id),
  participantName: schema.participant_name,
  participantImage: schema.participant_image,
  participantWallet: schema.participant_wallet,
  participantDisplayName: schema.participant_displayname,
  price: castNumOrUndefined(schema.price),
  extra: schema.extra,
});

export const getPendingActivityDataFromSchema = (schema: PendingActivitySchema): PendingActivityData => ({
  ...getActivityDataFromSchema(schema),
  pending: schema.pending,
  pendingNotifHeader: schema.pending_notif_header,
  pendingNotifMessage: schema.pending_notif_message,
});

export const getActivitySchemaFromData = (data: ActivityData): ActivitySchema => ({
  activity_id: data.activityId,
  type: data.type,
  user_id: data.userId,
  created_at: data.createdAt,
  read_at: data.readAt,
  username: data.username,
  offchain_game_id: data.offChainGameId,
  game_id: data.gameId,
  gem_name: data.gemName,
  gem_type: data.gemType,
  participant_id: data.participantId,
  participant_name: data.participantName,
  participant_displayname: data.participantDisplayName,
  participant_image: data.participantImage,
  participant_wallet: data.participantWallet,
  price: data.price,
  extra: data.extra,
});

export const getPendingActivitySchemaFromData = (data: PendingActivityData): PendingActivitySchema => ({
  ...getActivitySchemaFromData(data),
  pending: data.pending,
  pending_notif_header: data.pendingNotifHeader,
  pending_notif_message: data.pendingNotifMessage,
});

export const getInsertActivitySchemaFromData = (data: InsertActivityData): InsertActivitySchema => ({
  type: data.type,
  user_id: data.userId,
  created_at: data.createdAt,
  read_at: data.readAt,
  username: data.username,
  offchain_game_id: data.offChainGameId,
  game_id: data.gameId,
  gem_name: data.gemName,
  gem_type: data.gemType,
  participant_id: data.participantId,
  participant_name: data.participantName,
  participant_displayname: data.participantDisplayName,
  participant_image: data.participantImage,
  participant_wallet: data.participantWallet,
  price: data.price,
  extra: data.extra,
});

export const getInsertPendingActivitySchemaFromData = (data: InsertPendingActivityData): InsertPendingActivitySchema => ({
  ...getInsertActivitySchemaFromData(data),
  pending: data.pending,
  pending_notif_header: data.pendingNotifHeader,
  pending_notif_message: data.pendingNotifMessage,
});

export function isPendingActivityData(data: ActivityData): data is PendingActivityData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Boolean((data as any)?.pending);
}

export function isPendingActivitySchema(data: ActivitySchema): data is PendingActivitySchema {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Boolean((data as any)?.pending);
}
